<template>
  <SettlementDrawerShell
    :show="show"
    title="批次详情"
    description="查看本次月结的批次信息与明细记录。"
    @close="emit('close')"
  >
    <div v-if="loading" class="flex min-h-[280px] items-center justify-center">
      <div class="text-center">
        <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
        <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载批次详情...</p>
      </div>
    </div>

    <div v-else-if="detail" class="space-y-6">
      <section class="grid grid-cols-1 gap-4 rounded-2xl bg-gray-50 p-4 dark:bg-gray-900/50 md:grid-cols-2">
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">租户</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ tenantName }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">结算月份</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ detail.batch.settlementMonth.slice(0, 7) }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">批次号</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">#{{ detail.batch.id }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">结算时间</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ formatDateTime(detail.batch.confirmedAt) }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">记录数</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ detail.batch.recordCount }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">应结租户</p>
          <p class="mt-1 text-lg font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(detail.batch.tenantAmount) }}</p>
        </div>
      </section>

      <section class="grid grid-cols-1 gap-4 rounded-2xl border border-gray-200 p-4 dark:border-gray-700 md:grid-cols-3">
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">用户实付</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ formatCurrency(detail.batch.grossAmount) }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">平台留存</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ formatCurrency(detail.batch.platformAmount) }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">应结租户</p>
          <p class="mt-1 text-base font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(detail.batch.tenantAmount) }}</p>
        </div>
      </section>

      <section class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div class="rounded-2xl border border-gray-200 px-4 py-3 dark:border-gray-700">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">打款流水号</p>
          <p class="mt-1 text-sm text-gray-700 dark:text-gray-300">{{ detail.batch.paymentReference || '未填写' }}</p>
        </div>
        <div class="rounded-2xl border border-gray-200 px-4 py-3 dark:border-gray-700">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">结算备注</p>
          <p class="mt-1 text-sm text-gray-700 dark:text-gray-300">{{ detail.batch.note || '未填写' }}</p>
        </div>
      </section>

      <section class="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700">
        <div class="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/50">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">批次明细</h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-white dark:bg-gray-800">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">记录ID</th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">来源</th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">订单号</th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">用户ID</th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">用户实付</th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">平台留存</th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">应结租户</th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">生效时间</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              <tr v-for="record in detail.records" :key="record.id" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                <td class="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{{ record.id }}</td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{{ getSourceLabel(record.sourceType, record.recordKind) }} #{{ record.sourceId }}</td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{{ record.sourceOrderNo }}</td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{{ record.userId }}</td>
                <td class="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">{{ formatCurrency(record.grossAmount) }}</td>
                <td class="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">{{ formatCurrency(record.platformAmount) }}</td>
                <td class="px-4 py-3 text-right text-sm font-medium text-emerald-600 dark:text-emerald-400">{{ formatCurrency(record.tenantAmount) }}</td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{{ formatDateTime(record.paidAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </SettlementDrawerShell>
</template>

<script setup lang="ts">
import SettlementDrawerShell from '@/views/admin/components/tenant-settlement/SettlementDrawerShell.vue'
import type { TenantSettlementBatchDetailResponse } from '@/types/admin/tenant'
import { formatCurrency, formatDateTime } from '@/utils/formatters'

interface Props {
  show: boolean
  tenantName: string
  detail: TenantSettlementBatchDetailResponse | null
  loading: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  close: []
}>()

function getSourceLabel(sourceType: string, recordKind: string): string {
  const sourceLabel = sourceType === 'TUTOR_LESSON' ? '约课' : '会员'
  return recordKind === 'REFUND_ADJUSTMENT' ? `${sourceLabel}退款调整` : sourceLabel
}
</script>
